import React, { useEffect, useRef, useState } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import toast from "react-hot-toast";
import { useGooglePlace } from "context/GooglePlaceContext";

const libraries = ["places"];

function isManualPlaceInput(value) {
  const text = String(value || "").trim();
  if (!text) return false;
  if (
    /^https?:\/\/.*(google\.[a-z.]+\/maps|maps\.google\.|goo\.gl\/maps|maps\.app\.goo\.gl|g\.page)/i.test(
      text
    ) ||
    /^(www\.)?(google\.[a-z.]+\/maps|maps\.google\.|maps\.app\.goo\.gl|goo\.gl\/maps|g\.page)/i.test(
      text
    )
  ) {
    return true;
  }
  if (/place_id[:=](?:ChI|EhI|GhI)[\w-]+/i.test(text)) return true;
  return /^(?:ChI|EhI|GhI)[A-Za-z0-9_-]{16,}$/.test(text);
}

function predictionText(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.text === "string") return value.text;
  if (typeof value.toString === "function") {
    const text = value.toString();
    if (text && text !== "[object Object]") return text;
  }
  return "";
}

function placeDisplayName(place) {
  if (!place) return "";
  if (typeof place.displayName === "string") return place.displayName;
  return place.displayName?.text || place.name || "";
}

function isPlacesNewUnavailable(error) {
  const message = String(error?.message || error || "").toLowerCase();
  if (message.includes("legacyapinotactivated")) return false;
  if (message.includes("has not been used") || message.includes("it is disabled")) return true;
  if (message.includes("is not enabled") || message.includes("service_disabled")) return true;
  if (message.includes("api_key_service_blocked")) return true;
  return message.includes("permission_denied") && message.includes("places");
}

async function loadPlacesLibrary() {
  if (window.google?.maps?.importLibrary) {
    return window.google.maps.importLibrary("places");
  }
  return window.google?.maps?.places || null;
}

function resolvePlaceNameLegacy(placeId) {
  return new Promise((resolve) => {
    if (!window.google?.maps?.places?.PlacesService) {
      resolve(null);
      return;
    }

    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );
    service.getDetails(
      { placeId, fields: ["place_id", "name"] },
      (result, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          result
        ) {
          resolve({
            name: result.name || "",
            placeId: result.place_id || placeId,
          });
          return;
        }
        resolve(null);
      }
    );
  });
}

const GoogleReviews = ({ formData, handleRefresh }) => {
  const { setPlaceId } = useGooglePlace();

  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [placesMode, setPlacesMode] = useState("new");

  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const listenerRef = useRef(null);
  const formDataRef = useRef(formData);
  const manualLockRef = useRef("");
  const savingRef = useRef(false);
  const placesModeRef = useRef("new");
  const sessionTokenRef = useRef(null);
  const suggestSeqRef = useRef(0);
  const suggestTimerRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const AppService = useAppServices();
  const [agency] = useAgencyInfo();

  const apiKey = agency?.google_api_key;
  const savedPlaceId = formData?.google_place_id || "";
  const savedPlaceUrl = formData?.google_place_url || "";

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey || "",
    libraries,
    version: "weekly",
  });

  const switchToLegacy = () => {
    if (placesModeRef.current === "legacy") return;
    placesModeRef.current = "legacy";
    setSuggestions([]);
    setActiveIndex(-1);
    setPlacesMode("legacy");
  };

  const savePlaceId = async (nextPlaceId) => {
    const accountId = formDataRef.current?.id ?? formDataRef.current?._id;

    if (!accountId) return;

    const valueToSave = nextPlaceId ?? "";
    const savedId = formDataRef.current?.google_place_id ?? "";
    const savedUrl = formDataRef.current?.google_place_url ?? "";

    if (
      String(valueToSave) === String(savedId) ||
      (valueToSave && String(valueToSave) === String(savedUrl))
    ) {
      return;
    }

    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = {
        _id: accountId,
        google_place_id: valueToSave,
      };

      const { response, error } =
        await AppService.accounts.update({ payload });

      if (error || response?.success === false) {
        toast.error(
          error?.message ||
            response?.message ||
            "Failed to save Google Business Profile"
        );
        return;
      }

      await handleRefresh?.();

      toast.success(
        valueToSave
          ? "Google Business Profile saved"
          : "Google Business Profile removed"
      );
    } catch (err) {
      toast.error("Failed to save Google Business Profile");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const resolvePlaceName = async (placeId) => {
    if (placesModeRef.current === "legacy") {
      return resolvePlaceNameLegacy(placeId);
    }

    try {
      const lib = await loadPlacesLibrary();
      if (!lib?.Place) return { unavailable: true };

      const nextPlace = new lib.Place({ id: placeId });
      await nextPlace.fetchFields({ fields: ["displayName", "id"] });
      return {
        name: placeDisplayName(nextPlace),
        placeId: nextPlace.id || placeId,
      };
    } catch (error) {
      if (isPlacesNewUnavailable(error)) return { unavailable: true };
      return null;
    }
  };

  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;

    (async () => {
      try {
        const lib = await loadPlacesLibrary();
        if (cancelled) return;
        if (!lib?.AutocompleteSuggestion || !lib?.Place) switchToLegacy();
      } catch (error) {
        if (!cancelled && isPlacesNewUnavailable(error)) switchToLegacy();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded]);

  useEffect(() => {
    let cancelled = false;

    if (!savedPlaceId) {
      setInputValue("");
      setPlaceId(null);
      return;
    }

    setPlaceId(savedPlaceId);
    if (!isLoaded) return;

    (async () => {
      let loaded = await resolvePlaceName(savedPlaceId);
      if (cancelled) return;

      if (loaded?.unavailable) {
        switchToLegacy();
        loaded = await resolvePlaceNameLegacy(savedPlaceId);
        if (cancelled) return;
      }

      if (loaded?.name) {
        setInputValue(loaded.name);
        manualLockRef.current = "";
        return;
      }

      setInputValue(savedPlaceUrl || savedPlaceId);
    })();

    return () => {
      cancelled = true;
    };
  }, [savedPlaceId, savedPlaceUrl, isLoaded, setPlaceId]);

  useEffect(() => {
    if (!isLoaded || placesMode !== "legacy") return;
    if (!window.google?.maps?.places || !inputRef.current) return;
    if (autocompleteRef.current) return;

    const input = inputRef.current;
    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      input,
      {
        fields: ["place_id", "name", "geometry"],
      }
    );

    // A rejected Places request (localhost is not on the website key's
    // referrer list) makes the Autocomplete widget set disabled itself.
    const releaseGoogleLock = () => {
      if (savingRef.current || !input.disabled) return;
      input.disabled = false;
    };
    const lockObserver = new MutationObserver(releaseGoogleLock);
    lockObserver.observe(input, {
      attributes: true,
      attributeFilter: ["disabled"],
    });
    const lockTimer = window.setInterval(releaseGoogleLock, 200);

    listenerRef.current = autocompleteRef.current.addListener(
      "place_changed",
      () => {
        const typed = inputRef.current?.value?.trim() || manualLockRef.current;
        if (isManualPlaceInput(typed)) {
          setInputValue(typed);
          return;
        }

        const selectedPlace = autocompleteRef.current.getPlace();

        if (!selectedPlace?.place_id) {
          setPlaceId(null);
          setInputValue("");
          return;
        }

        setPlaceId(selectedPlace.place_id);
        setInputValue(selectedPlace.name || "");
        savePlaceId(selectedPlace.place_id);
      }
    );

    return () => {
      lockObserver.disconnect();
      window.clearInterval(lockTimer);

      if (listenerRef.current) {
        window.google.maps.event.removeListener(listenerRef.current);
      }

      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current
        );
      }

      autocompleteRef.current = null;
      listenerRef.current = null;
    };
  }, [isLoaded, placesMode]);

  useEffect(() => {
    return () => window.clearTimeout(suggestTimerRef.current);
  }, []);

  const loadSuggestions = async (text) => {
    const requestId = ++suggestSeqRef.current;

    try {
      const lib = await loadPlacesLibrary();
      if (!lib?.AutocompleteSuggestion || !lib?.AutocompleteSessionToken) {
        switchToLegacy();
        return;
      }

      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new lib.AutocompleteSessionToken();
      }

      const { suggestions: nextSuggestions } =
        await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: text,
          sessionToken: sessionTokenRef.current,
        });

      if (requestId !== suggestSeqRef.current) return;

      const mapped = (nextSuggestions || [])
        .map((suggestion) => {
          const prediction = suggestion.placePrediction;
          if (!prediction?.placeId) return null;
          const label = predictionText(prediction.text);
          const primary = predictionText(prediction.mainText) || label;
          const secondary = predictionText(prediction.secondaryText);
          return {
            placeId: prediction.placeId,
            primary,
            secondary,
            label: label || primary,
            prediction,
          };
        })
        .filter(Boolean);

      setSuggestions(mapped);
      setActiveIndex(mapped.length ? 0 : -1);
    } catch (error) {
      if (requestId !== suggestSeqRef.current) return;
      setSuggestions([]);
      setActiveIndex(-1);
      if (isPlacesNewUnavailable(error)) switchToLegacy();
    }
  };

  const scheduleSuggest = (value) => {
    window.clearTimeout(suggestTimerRef.current);
    if (placesModeRef.current !== "new") return;

    const text = String(value || "").trim();
    if (!text || isManualPlaceInput(text)) {
      suggestSeqRef.current += 1;
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }

    suggestTimerRef.current = window.setTimeout(() => {
      loadSuggestions(text);
    }, 300);
  };

  const selectSuggestion = async (item) => {
    if (!item?.placeId) return;

    suggestSeqRef.current += 1;
    setSuggestions([]);
    setActiveIndex(-1);

    const fallbackName = item.label || item.primary || "";
    setInputValue(fallbackName);
    setPlaceId(item.placeId);
    manualLockRef.current = "";

    try {
      const selected = item.prediction?.toPlace?.();
      if (selected?.fetchFields) {
        await selected.fetchFields({ fields: ["displayName", "id"] });
        const name = placeDisplayName(selected) || fallbackName;
        const id = selected.id || item.placeId;
        setPlaceId(id);
        setInputValue(name);
        sessionTokenRef.current = null;
        savePlaceId(id);
        return;
      }
    } catch (error) {
      if (isPlacesNewUnavailable(error)) switchToLegacy();
    }

    sessionTokenRef.current = null;
    savePlaceId(item.placeId);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;

    setInputValue(value);
    if (!isManualPlaceInput(value)) {
      manualLockRef.current = "";
    }

    if (!value.trim()) {
      setPlaceId(null);
      setSuggestions([]);
      setActiveIndex(-1);
    }

    scheduleSuggest(value);
  };

  const commitManualPlace = (value) => {
    const text = String(value || "").trim();
    if (!isManualPlaceInput(text)) return;
    manualLockRef.current = text;
    setSuggestions([]);
    setInputValue(text);
    savePlaceId(text);
  };

  const handlePaste = (event) => {
    const text = event.clipboardData?.getData("text")?.trim() || "";
    if (!isManualPlaceInput(text)) return;
    manualLockRef.current = text;
    setSuggestions([]);
    window.setTimeout(() => commitManualPlace(text), 0);
  };

  const handleInputBlur = () => {
    setSuggestions([]);
    setActiveIndex(-1);

    const text = inputValue.trim();
    if (!text && formDataRef.current?.google_place_id) {
      manualLockRef.current = "";
      savePlaceId("");
      return;
    }
    if (isManualPlaceInput(text)) {
      commitManualPlace(text);
    }
  };

  const handleInputKeyDown = (event) => {
    if (placesModeRef.current === "new" && suggestions.length) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, suggestions.length - 1));
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
        return;
      }
      if (event.key === "Escape") {
        setSuggestions([]);
        setActiveIndex(-1);
        return;
      }
      if (event.key === "Enter" && activeIndex >= 0) {
        event.preventDefault();
        event.stopPropagation();
        selectSuggestion(suggestions[activeIndex]);
        return;
      }
    }

    if (event.key !== "Enter" || !isManualPlaceInput(event.currentTarget.value)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    commitManualPlace(event.currentTarget.value);
  };

  if (!apiKey) {
    return (
      <div className="text-danger">
        Google Maps API Key is not configured.
      </div>
    );
  }

  return (
    <div style={{ marginBottom: "20px" }}>
      <label className="text-black">
        Link to Google Business Profile
      </label>

      <div style={{ position: "relative" }}>
        <input
          ref={inputRef}
          id="place-input"
          type="text"
          className="form-control-agust"
          placeholder="Business name, Place ID, or Google Maps link"
          value={inputValue}
          disabled={saving}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onPaste={handlePaste}
          onKeyDown={handleInputKeyDown}
          autoComplete="off"
        />
        {placesMode === "new" && suggestions.length > 0 && (
          <ul
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              border: "1px solid #ddd",
              background: "#fff",
              position: "absolute",
              zIndex: 20,
              left: 0,
              right: 0,
              maxHeight: 240,
              overflowY: "auto",
            }}
          >
            {suggestions.map((item, index) => (
              <li
                key={item.placeId}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectSuggestion(item);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  background: index === activeIndex ? "#f3f4f6" : "#fff",
                }}
              >
                <div>{item.primary}</div>
                {item.secondary ? (
                  <small className="text-muted">{item.secondary}</small>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      <small className="text-muted">
        Name search skips businesses with no public address. Paste a Place ID
        or a Google Maps link instead.
      </small>
    </div>
  );
};

export default GoogleReviews;
